import React, { forwardRef, memo, useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector, useTranslate, useWidgetLocalParams } from '@src/core/hooks';
import { useWidgetId } from '@src/core/IdProvider';
import MultiProductBookingModal, {
  MultiProductBookingModalRef,
} from '@src/domains/products/features/multiProductBooking/MultiProductBookingModal';
import {
  addNextProductPackageToAdderState,
  addPackageToCart,
  checkIfThePackageHasInactiveProductSelector,
  closeAdderModal,
  productPackageErrorSelector,
  productPackageSelector,
} from '@src/stores/product';
import {
  startPackageMultiProductBooking,
  closeMultiProductBookingModal,
  multiProductBookingSelector,
} from '@src/stores/multiProductBooking';
import usePackageIdParam from '@src/hooks/usePackageIdParam';
import usePackageBooking from '@src/hooks/usePackageBooking';
import { track } from '@src/services/analytics';

const PackageBookingModal = forwardRef<MultiProductBookingModalRef>((_, ref) => {
  const widgetId = useWidgetId();
  const dispatch = useAppDispatch();
  const { i18n } = useTranslate();
  const packageId = usePackageIdParam()!;
  const params = useWidgetLocalParams();

  const { products, packageTotalCents } = usePackageBooking();
  const productPackage = useAppSelector(productPackageSelector(packageId));
  const error = useAppSelector(productPackageErrorSelector(packageId));
  const booking = useAppSelector(multiProductBookingSelector);
  const hasInactiveProduct = useAppSelector(checkIfThePackageHasInactiveProductSelector(packageId));

  useEffect(() => {
    if (products.length && !booking?.isOpen) {
      dispatch(startPackageMultiProductBooking(widgetId, packageId, params));
    }
  }, [products.length, booking?.isOpen, dispatch, widgetId, packageId, params]);

  const handleSelect = useCallback(
    (productId: string) => {
      dispatch(addNextProductPackageToAdderState(widgetId, productId));
    },
    [dispatch, widgetId],
  );

  const handleAddToCart = useCallback(() => {
    dispatch(addPackageToCart(widgetId));
  }, [dispatch, widgetId]);

  const handleConfirmAbandon = useCallback(() => {
    track('productPackageCancelConfirm');
    dispatch(closeMultiProductBookingModal());
    dispatch(closeAdderModal(widgetId, packageId));
  }, [dispatch, widgetId, packageId]);

  return (
    <MultiProductBookingModal
      ref={ref}
      dataCy='product-package-adder-modal'
      title={productPackage?.name}
      products={products}
      onSelect={handleSelect}
      showListTotal
      completedDescription={(count) =>
        i18n('product_package_adder_complete_selection_description', {
          packageName: productPackage?.name ?? '',
          productQuantity: String(count),
        })
      }
      totalCents={packageTotalCents}
      onAddToCart={handleAddToCart}
      onConfirmAbandon={handleConfirmAbandon}
      cancelTitle={i18n('product_package_cancel_modal_title')}
      cancelDescription={i18n('product_package_cancel_modal_description')}
      cancelConfirmLabel={i18n('product_package_cancel_modal_action_yes')}
      cancelCancelLabel={i18n('product_package_cancel_modal_action_no')}
      alwaysConfirmClose={false}
      showProductListWhenCompleted
      disableAllProducts={hasInactiveProduct}
      error={error}
    />
  );
});

PackageBookingModal.displayName = 'PackageBookingModal';

export default memo(PackageBookingModal);
